/**
 * Per-module configuration for the period takeover selection (LIX-1696).
 *
 * Both modules run the same shell: two Vorlagen, a freely selectable area list,
 * a field grid for the employee details and the visibility choice. What differs
 * is the area list, which employee fields exist and the strings, so all of that
 * lives here and PeriodTakeover renders it.
 *
 * The preset is DERIVED from the selection and never stored: changing a field
 * away from Vollständig and back has to land on Vollständig again, which a
 * sticky flag cannot do.
 *
 * Every string is named explicitly rather than assembled from the module name.
 * Several of these texts are identical across the modules today and are shared
 * on purpose; the ones that differ (Einstellungen, the never-copied list, the
 * Minimal description) point at their own key. Patching a module name into one
 * shared string is what produced the wrong takeover text before.
 */

const K = "period_management.takeover";

/**
 * One row in the field grid.
 *
 * `slugs` is what goes to the backend. It is a list because a row may stand for
 * several field_settings entries: the LOB Referenzwerte row is one tick for
 * three columns that are never chosen apart.
 */
const field = (key, slugs) => ({ key, slugs: slugs || [key] });

/** Structural blocks, in the order the copy engine builds them. */
const AREAS = {
  /**
   * `summaryDescKey` is the same area described on the summary step, where the
   * selection wording no longer holds: the global roles are stated there as
   * something that IS there rather than as something that is not copied, the
   * groups sentence about rebuilding assignments belongs to the not-carried
   * column, and "steuert die Feldauswahl darunter" points at a control that is
   * one step back. Areas without one reuse `descKey`.
   */
  globalRoles: {
    key: "global_roles",
    labelKey: `${K}.area.global_roles`,
    descKey: `${K}.area.global_roles.desc`,
    summaryDescKey: `${K}.summary.area.global_roles.desc`,
  },
  settingsDb: { key: "settings", labelKey: `${K}.area.settings`, descKey: `${K}.area.settings.desc` },
  settingsLob: {
    key: "settings",
    labelKey: `${K}.area.settings`,
    descKey: `${K}.area.settings.desc.lob`,
    droppedDescKey: `${K}.summary.area.settings.desc.lob.dropped`,
  },
  groupsDb: {
    key: "groups",
    labelKey: `${K}.area.groups`,
    descKey: `${K}.area.groups.desc.db`,
    summaryDescKey: `${K}.summary.area.groups.desc.db`,
  },
  groupsLob: {
    key: "groups",
    labelKey: `${K}.area.groups`,
    descKey: `${K}.area.groups.desc.lob`,
    summaryDescKey: `${K}.summary.area.groups.desc.lob`,
  },
  // Same table, same wave, but the budget tables have no category column at all:
  // categories are the lookup behind groups.category and employees.category.
  employees: {
    key: "employees",
    labelKey: `${K}.area.employees`,
    descKey: `${K}.area.employees.desc`,
    summaryDescKey: `${K}.summary.area.employees.desc`,
  },
  budgets: {
    key: "budgets",
    labelKey: `${K}.area.budgets`,
    descKey: `${K}.area.budgets.desc`,
  },
  scorecards: {
    key: "scorecards",
    labelKey: `${K}.area.scorecards`,
    descKey: `${K}.area.scorecards.desc`,
    implicitItems: [{
      key: "scorecard_templates",
      labelKey: `${K}.implicit.scorecard_templates`,
      descKey: `${K}.implicit.scorecard_templates.desc`,
      noteKey: `${K}.implicit.always_copied`,
    }],
  },
};

/**
 * The employee details a DB takeover can choose.
 *
 * Slugs are field_settings.slug. The authoritative list and its per-customer
 * defaults belong in field_settings (state_enabled = 1, minus the fields that
 * are never carried over); this constant is the interim until that endpoint
 * exists. Every entry here is state_enabled = 1 today.
 *
 * Not selectable, and deliberately so:
 * - last_name, first_name, employee_number, email, employment_entry_date belong
 *   to the person and not to the period
 * - evaluation_period_from/_to, assessment_promotion_period_date,
 *   transfer_higher_level_tasks_date, reason_for_event_based_evaluation and
 *   prev_rbu_old_scale describe events of the period that ended
 * - previous_evaluation_results is looked up, never copied
 */
const DB_FIELDS = [
  field("department_staff_unit"),
  field("employee_group"),
  field("working_hours"),
  field("gender"),
  field("equal_opportunities_severe_disability_checkbox"),
  field("pay_grade"),
  field("job_differs_from_classification"),
  field("job_title"),
  field("job_description_text"),
];

/**
 * The employee details a LOB takeover can choose.
 *
 * Every entry below is `fixed` or `calculation_related`, which is what makes
 * the list ours to name. Check field_type before adding one.
 *
 * Beträge, Auszahlungen, Fehlmonate and Fehltage are not here and are not
 * meant to be: they belong to the period that ended.
 */
const LOB_FIELDS = [
  field("department"),
  field("gender"),
  field("pay_grade"),
  field("pay_grade_level"),
  field("working_hours"),
  field("working_days_per_week"),
  field("annual_compensation"),
  field("individual_pay_factor"),
  field("reference_values", [
    "reference_balanced_scorecard_setting_name",
    "reference_budget_name",
    "reference_group_name",
  ]),
];

const keysOf = (entries) => entries.map((e) => e.key);

function build(config) {
  const areaKeys = keysOf(config.areas);
  const fieldKeys = keysOf(config.fields);

  return {
    ...config,
    presets: {
      minimal: { areas: ["settings"], fields: [], visible: true },
      full: { areas: areaKeys, fields: fieldKeys, visible: true },
    },
  };
}

/**
 * The four global roles (IT-Administration, Programmadministration,
 * Moduladministration, Personalverwaltung) are not period bound: users, roles
 * and model_has_roles carry no period_id, so they are active in every period
 * whatever the takeover does. Nothing copies them, which is why they are not in
 * the payload either. They are shown as one fixed row so nobody reads their
 * absence from the list as "these are gone" on a screen that cannot be
 * corrected afterwards.
 *
 * "Nutzende" is not the word for them. Every person in the system is a Nutzende,
 * so a row called Weitere Nutzende said nothing and read as Personalverwaltung,
 * which is one of the four rather than something else.
 *
 * What IS period bound is who evaluates whom, and that is a real choice rather
 * than a requirement: group_evaluators, group_secondary_evaluators and
 * group_reviewers all carry period_id and all sit under the Gruppen area. The
 * two modules name those people differently on purpose, LOB Bewertende and
 * Rezensierende against DB Erst- and Zweitbeurteilende, so the description
 * carries a key per module.
 *
 * Being outside the selectable set is also what keeps the preset comparison
 * honest: a value nobody can change must not decide whether a selection counts
 * as Vollständig.
 */
export const DB_TAKEOVER = build({
  module: "db",
  introKey: `${K}.intro_db`,
  fixedAreas: [AREAS.globalRoles],
  areas: [AREAS.settingsDb, AREAS.groupsDb, AREAS.employees],
  fieldsArea: "employees",
  fields: DB_FIELDS,
  presetDescKeys: {
    minimal: `${K}.preset_desc.minimal`,
    full: `${K}.preset_desc.full`,
    custom: `${K}.preset_desc.custom`,
  },
  alwaysBodyKey: `${K}.always_body`,
  neverBodyKey: `${K}.never_body`,
  previousResult: true,
});

export const LOB_TAKEOVER = build({
  module: "lob",
  introKey: `${K}.intro_lob`,
  fixedAreas: [AREAS.globalRoles],
  areas: [
    AREAS.settingsLob,
    AREAS.groupsLob,
    AREAS.budgets,
    AREAS.employees,
    AREAS.scorecards,
  ],
  fieldsArea: "employees",
  fields: LOB_FIELDS,
  presetDescKeys: {
    minimal: `${K}.preset_desc.minimal.lob`,
    full: `${K}.preset_desc.full`,
    custom: `${K}.preset_desc.custom`,
  },
  alwaysBodyKey: `${K}.always_body`,
  neverBodyKey: `${K}.never_body.lob`,
  previousResult: false,
});

export const takeoverConfigFor = (isDb) => (isDb ? DB_TAKEOVER : LOB_TAKEOVER);

export const defaultTakeover = (config) => ({ ...config.presets.full });

const sameSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

/** The preset a selection corresponds to, or null when it matches none. */
export function matchPreset(config, value) {
  return Object.keys(config.presets).find((name) => {
    const preset = config.presets[name];
    if (!sameSet(value.areas, preset.areas)) return false;
    if (value.visible !== preset.visible) return false;
    // Without the employees block the field selection is unreachable, so it does not count.
    if (!value.areas.includes(config.fieldsArea)) return true;
    return sameSet(value.fields, preset.fields);
  }) || null;
}

/**
 * The takeover split into what comes along and what does not (LIX-1706).
 *
 * Derived from the value the form posts, so the summary cannot describe a
 * different takeover than the one that runs. Without the employees block the
 * field selection is unreachable: the fields are then neither carried nor
 * individually dropped, because the whole area is, and listing them twice would
 * count the same loss two ways.
 */
export function summariseTakeover(config, value) {
  const employeesOn = value.areas.includes(config.fieldsArea);

  return {
    carried: config.areas.filter((a) => value.areas.includes(a.key)),
    dropped: config.areas.filter((a) => !value.areas.includes(a.key)),
    employeesOn,
    fieldsCarried: employeesOn ? config.fields.filter((f) => value.fields.includes(f.key)) : [],
    fieldsDropped: employeesOn ? config.fields.filter((f) => !value.fields.includes(f.key)) : [],
  };
}

export function takeoverPayload(config, value) {
  const { carried, employeesOn, fieldsCarried } = summariseTakeover(config, value);

  return {
    copy_mode: matchPreset(config, value) ?? "custom",
    copy_areas: carried.map((a) => a.key),
    copy_fields: fieldsCarried.flatMap((f) => f.slugs),
    employees_visible: employeesOn ? value.visible : null,
  };
}
